import { useCallback, useEffect, useReducer, useRef } from "react";
import type { AskScript } from "./askScript";
import type {
    AssistantState,
    AssistantTab,
    ChatMessage,
    QueuedDoc,
    QueuedQuestion,
} from "./types";

let messageCounter = 0;
const nextMessageId = () => `am-${++messageCounter}`;

let queueCounter = 0;
const nextQueueId = () => `qq-${++queueCounter}`;

const initialState = (): AssistantState => ({
    messages: [],
    cursor: 0,
    recordingTranscript: null,
    queuedQuestions: [],
    queuedDocs: [],
    activeTab: "chat",
    submitted: false,
});

type Action =
    | { type: "APPEND_MESSAGE"; message: ChatMessage }
    | { type: "REPLACE_MESSAGE"; id: string; message: ChatMessage }
    | { type: "PATCH_ATTACHMENT"; id: string; patch: Partial<NonNullable<ChatMessage["attachment"]>> }
    | { type: "MARK_QUEUED"; exchangeIdx: number }
    | { type: "SET_RECORDING_TRANSCRIPT"; value: string | null }
    | { type: "ADVANCE_CURSOR" }
    | { type: "ADD_QUEUED_QUESTION"; item: QueuedQuestion }
    | { type: "EDIT_QUEUED_QUESTION"; id: string; text: string }
    | { type: "REMOVE_QUEUED_QUESTION"; id: string }
    | { type: "ADD_QUEUED_DOC"; doc: QueuedDoc }
    | { type: "REMOVE_QUEUED_DOC"; id: string }
    | { type: "SET_TAB"; tab: AssistantTab }
    | { type: "SUBMIT" }
    | { type: "RESET" };

function reducer(state: AssistantState, action: Action): AssistantState {
    switch (action.type) {
        case "APPEND_MESSAGE":
            return { ...state, messages: [...state.messages, action.message] };
        case "REPLACE_MESSAGE":
            return {
                ...state,
                messages: state.messages.map((m) =>
                    m.id === action.id ? action.message : m
                ),
            };
        case "PATCH_ATTACHMENT":
            return {
                ...state,
                messages: state.messages.map((m) =>
                    m.id === action.id && m.attachment
                        ? { ...m, attachment: { ...m.attachment, ...action.patch } }
                        : m
                ),
            };
        case "MARK_QUEUED":
            return {
                ...state,
                messages: state.messages.map((m) =>
                    m.exchangeIdx === action.exchangeIdx ? { ...m, queued: true } : m
                ),
            };
        case "SET_RECORDING_TRANSCRIPT":
            return { ...state, recordingTranscript: action.value };
        case "ADVANCE_CURSOR":
            return { ...state, cursor: state.cursor + 1 };
        case "ADD_QUEUED_QUESTION":
            return { ...state, queuedQuestions: [...state.queuedQuestions, action.item] };
        case "EDIT_QUEUED_QUESTION":
            return {
                ...state,
                queuedQuestions: state.queuedQuestions.map((q) =>
                    q.id === action.id ? { ...q, text: action.text } : q
                ),
            };
        case "REMOVE_QUEUED_QUESTION":
            return {
                ...state,
                queuedQuestions: state.queuedQuestions.filter((q) => q.id !== action.id),
            };
        case "ADD_QUEUED_DOC":
            return { ...state, queuedDocs: [...state.queuedDocs, action.doc] };
        case "REMOVE_QUEUED_DOC":
            return {
                ...state,
                queuedDocs: state.queuedDocs.filter((d) => d.id !== action.id),
            };
        case "SET_TAB":
            return { ...state, activeTab: action.tab };
        case "SUBMIT":
            return { ...state, submitted: true };
        case "RESET":
            messageCounter = 0;
            queueCounter = 0;
            return initialState();
    }
}

export function useAskMachine(script: AskScript) {
    const [state, dispatch] = useReducer(reducer, undefined, initialState);
    const scriptRef = useRef(script);
    scriptRef.current = script;
    const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
    const intervalsRef = useRef<Set<ReturnType<typeof setInterval>>>(new Set());

    const setTrackedTimeout = useCallback((fn: () => void, ms: number) => {
        const id = setTimeout(() => {
            timersRef.current.delete(id);
            fn();
        }, ms);
        timersRef.current.add(id);
        return id;
    }, []);

    const setTrackedInterval = useCallback((fn: () => void, ms: number) => {
        const id = setInterval(fn, ms);
        intervalsRef.current.add(id);
        return id;
    }, []);

    const clearTrackedInterval = useCallback((id: ReturnType<typeof setInterval>) => {
        clearInterval(id);
        intervalsRef.current.delete(id);
    }, []);

    const clearAllTimers = useCallback(() => {
        timersRef.current.forEach((id) => clearTimeout(id));
        timersRef.current.clear();
        intervalsRef.current.forEach((id) => clearInterval(id));
        intervalsRef.current.clear();
    }, []);

    useEffect(() => {
        return () => {
            clearAllTimers();
        };
    }, [clearAllTimers]);

    // Greet on first mount.
    const greetedRef = useRef(false);
    useEffect(() => {
        if (greetedRef.current) return;
        greetedRef.current = true;
        dispatch({
            type: "APPEND_MESSAGE",
            message: {
                id: nextMessageId(),
                role: "bot",
                kind: "greeting",
                text: scriptRef.current.greeting,
            },
        });
    }, []);

    const botReply = useCallback(
        (message: Omit<ChatMessage, "id" | "role" | "typing">) => {
            const typingId = nextMessageId();
            dispatch({
                type: "APPEND_MESSAGE",
                message: { id: typingId, role: "bot", kind: message.kind, typing: true },
            });
            setTrackedTimeout(() => {
                dispatch({
                    type: "REPLACE_MESSAGE",
                    id: typingId,
                    message: { ...message, id: typingId, role: "bot" },
                });
            }, 600);
        },
        [setTrackedTimeout]
    );

    /** Type out a transcript into `recordingTranscript` to simulate a live mic.
     *  Mirrors the intake demo's simulateVoice helper. */
    const simulateVoice = useCallback(
        (transcript: string, onDone: () => void) => {
            dispatch({ type: "SET_RECORDING_TRANSCRIPT", value: "" });
            setTrackedTimeout(() => {
                let i = 0;
                const stepMs = 22;
                const interval = setTrackedInterval(() => {
                    i += Math.max(3, Math.floor(Math.random() * 7));
                    const slice = transcript.slice(0, Math.min(i, transcript.length));
                    dispatch({ type: "SET_RECORDING_TRANSCRIPT", value: slice });
                    if (i >= transcript.length) {
                        clearTrackedInterval(interval);
                        setTrackedTimeout(() => {
                            dispatch({ type: "SET_RECORDING_TRANSCRIPT", value: null });
                            onDone();
                        }, 600);
                    }
                }, stepMs);
            }, 700);
        },
        [setTrackedInterval, clearTrackedInterval, setTrackedTimeout]
    );

    /** Submit the next user message in the script and trigger the bot's reply.
     *  `mode` controls whether the user message arrives instantly (chip click)
     *  or after a simulated mic recording. */
    const runNextExchange = useCallback(
        (mode: "typed" | "spoken") => {
            const idx = state.cursor;
            const exchanges = scriptRef.current.exchanges;
            const exchange = exchanges[idx];
            if (!exchange) return;

            const isLast = idx === exchanges.length - 1;
            const closingPrompt = scriptRef.current.closingPrompt;
            const send = () => {
                dispatch({
                    type: "APPEND_MESSAGE",
                    message: {
                        id: nextMessageId(),
                        role: "user",
                        kind: "user",
                        text: exchange.userText,
                        exchangeIdx: idx,
                    },
                });
                botReply({
                    kind: exchange.kind,
                    text: exchange.botText,
                    email: exchange.email,
                    exchangeIdx: idx,
                });
                dispatch({ type: "ADVANCE_CURSOR" });
                if (isLast) {
                    // After the last scripted reply lands, nudge the user toward
                    // uploading docs and submitting the queue.
                    setTrackedTimeout(() => {
                        botReply({ kind: "answer", text: closingPrompt });
                    }, 1400);
                }
            };

            if (mode === "spoken") {
                simulateVoice(exchange.userText, send);
            } else {
                send();
            }
        },
        [state.cursor, botReply, simulateVoice, setTrackedTimeout]
    );

    const queueExchange = useCallback(
        (exchangeIdx: number) => {
            const exchange = scriptRef.current.exchanges[exchangeIdx];
            if (!exchange) return;
            // Avoid double-queueing the same exchange.
            const alreadyQueued = state.queuedQuestions.some(
                (q) => q.text === exchange.userText
            );
            if (alreadyQueued) return;
            dispatch({
                type: "ADD_QUEUED_QUESTION",
                item: { id: nextQueueId(), text: exchange.userText },
            });
            dispatch({ type: "MARK_QUEUED", exchangeIdx });
        },
        [state.queuedQuestions]
    );

    const removeQueuedQuestion = useCallback((id: string) => {
        dispatch({ type: "REMOVE_QUEUED_QUESTION", id });
    }, []);

    const editQueuedQuestion = useCallback((id: string, text: string) => {
        dispatch({ type: "EDIT_QUEUED_QUESTION", id, text });
    }, []);

    const addQueuedQuestion = useCallback((text: string) => {
        dispatch({
            type: "ADD_QUEUED_QUESTION",
            item: { id: nextQueueId(), text },
        });
    }, []);

    const attachDoc = useCallback((doc: QueuedDoc) => {
        dispatch({ type: "ADD_QUEUED_DOC", doc });
    }, []);

    /** Attach a doc from the chat composer: push a doc message into the chat,
     *  animate its upload progress, then add it to the next-round queue and
     *  have the bot acknowledge. Mirrors the guided intake's upload feel
     *  without switching tabs. */
    const attachDocFromChat = useCallback(
        (filename: string, size: string) => {
            const messageId = nextMessageId();
            const docId = `qd-${++queueCounter}`;

            dispatch({
                type: "APPEND_MESSAGE",
                message: {
                    id: messageId,
                    role: "user",
                    kind: "doc",
                    attachment: {
                        filename,
                        size,
                        progress: 0,
                        uploading: true,
                    },
                },
            });

            const totalMs = 1400;
            const stepMs = 60;
            const totalSteps = Math.ceil(totalMs / stepMs);
            let step = 0;
            const interval = setTrackedInterval(() => {
                step += 1;
                const progress = Math.min(100, (step / totalSteps) * 100);
                dispatch({ type: "PATCH_ATTACHMENT", id: messageId, patch: { progress } });
                if (step >= totalSteps) {
                    clearTrackedInterval(interval);
                    dispatch({
                        type: "PATCH_ATTACHMENT",
                        id: messageId,
                        patch: { progress: 100, uploading: false },
                    });
                    dispatch({
                        type: "ADD_QUEUED_DOC",
                        doc: { id: docId, filename, size },
                    });
                    botReply({
                        kind: "answer",
                        text:
                            "Got it — I've added " +
                            filename +
                            " to the queue for the analyst.",
                    });
                }
            }, stepMs);
        },
        [setTrackedInterval, clearTrackedInterval, botReply]
    );

    const removeQueuedDoc = useCallback((id: string) => {
        dispatch({ type: "REMOVE_QUEUED_DOC", id });
    }, []);

    const setTab = useCallback((tab: AssistantTab) => {
        dispatch({ type: "SET_TAB", tab });
    }, []);

    const submit = useCallback(() => {
        dispatch({ type: "SUBMIT" });
    }, []);

    const reset = useCallback(() => {
        clearAllTimers();
        greetedRef.current = false;
        dispatch({ type: "RESET" });
    }, [clearAllTimers]);

    return {
        state,
        runNextExchange,
        queueExchange,
        editQueuedQuestion,
        addQueuedQuestion,
        removeQueuedQuestion,
        attachDoc,
        attachDocFromChat,
        removeQueuedDoc,
        setTab,
        submit,
        reset,
    };
}
